import React, { useState } from 'react';
import {
  HardHat,
  Shirt,
  Hand,
  PersonStanding,
  Shield,
  Layers,
  CircleDot,
  Square,
  Droplets,
  Spline,
  Feather,
  Package,
  Box,
  Mountain,
  Flame,
  Pin,
  Gem,
  Hammer,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { RECIPES, RECIPE_SETS } from '../mock';

const itemIcons = {
  HardHat,
  Shirt,
  Hand,
  PersonStanding,
  Shield,
};

const ingredientIcons = {
  Layers,
  CircleDot,
  Square,
  Droplets,
  Spline,
  Feather,
  Package,
  Box,
  Mountain,
  Flame,
  Pin,
  Gem,
};

const RecipeCard = ({ recipe, isOpen, onToggle }) => {
  const ItemIcon = itemIcons[recipe.icon] || Hammer;
  return (
    <div
      className={`rounded-xl border bg-black/45 transition-all duration-300 ${
        isOpen
          ? 'border-[#f5b400] shadow-[0_0_30px_-12px_rgba(245,180,0,0.55)]'
          : 'border-white/12 hover:border-[#f5b400]/50'
      }`}
    >
      <button onClick={onToggle} className="w-full flex items-center gap-4 p-5 text-left">
        <div
          className="w-14 h-14 rounded-lg flex items-center justify-center border border-white/10 shrink-0"
          style={{ background: `${recipe.accent}22` }}
        >
          <ItemIcon size={28} style={{ color: recipe.accent }} strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-white text-xl leading-tight">{recipe.name}</h3>
          <p className="font-nav text-[10px] tracking-widest text-white/55 mt-1">
            {recipe.slot.toUpperCase()} {'·'} {recipe.ingredients.length} INGREDIENTS
          </p>
        </div>
        <div className="text-[#f5b400] shrink-0">
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {isOpen && (
        <div className="px-5 pb-5 border-t border-white/8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            {recipe.ingredients.map((ing, i) => {
              const IngIcon = ingredientIcons[ing.icon] || Package;
              return (
                <div
                  key={i}
                  className="flex items-start gap-3 rounded-lg border border-white/8 bg-black/30 p-3 hover:border-[#f5b400]/40 transition-colors"
                >
                  <div className="w-9 h-9 rounded bg-white/5 flex items-center justify-center shrink-0 border border-white/10">
                    <IngIcon size={18} className="text-[#f5b400]" strokeWidth={1.6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-heading text-white text-sm">{ing.name}</span>
                      <span className="font-nav text-[10px] text-[#f5b400] bg-[#f5b400]/10 border border-[#f5b400]/30 rounded-full px-2 py-0.5 shrink-0">
                        x{ing.qty}
                      </span>
                    </div>
                    {ing.sub && (
                      <p className="font-body text-[11px] text-white/55 mt-0.5 italic">
                        Crafted from {ing.sub}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white/60 font-nav text-[10px] tracking-widest">
              <Hammer size={14} className="text-[#f5b400]" />
              ANVIL REQUIRED
            </div>
            <button className="font-nav text-[10px] tracking-widest text-black bg-[#f5b400] hover:bg-[#ffc630] px-4 py-2 rounded-md transition-colors">
              ADD TO QUEUE
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SetSection = ({ setDef, recipes, openId, setOpenId }) => {
  const isCorner = setDef.previewSize === 'corner';
  return (
    <div className="mb-16 relative">
      <div className="mb-8 text-center">
        <p className="font-nav text-[#f5b400] text-xs tracking-widest">RECIPES</p>
        <h2 className="font-heading section-heading-white mt-3">FORGE THE</h2>
        <h2 className="font-heading section-heading-gold">{setDef.name.toUpperCase()}</h2>
      </div>

      {setDef.preview && isCorner && (
        <div
          className="mb-8 sm:absolute sm:top-0 sm:right-0 sm:mb-0 sm:w-[180px] md:w-[210px] lg:w-[240px] mx-auto max-w-[200px]"
          data-testid="traveler-corner-preview"
        >
          <div className="relative rounded-xl border border-[#3e6b8a]/40 bg-gradient-to-b from-[#0e1a24]/70 via-black/40 to-black/70 p-3 shadow-[0_8px_30px_-12px_rgba(62,107,138,0.6)]">
            <div className="text-center mb-1.5">
              <p className="font-nav text-[9px] tracking-widest text-[#7fb3d5]">
                {setDef.previewLabel || 'PREVIEW'}
              </p>
            </div>
            <img
              src={setDef.preview}
              alt={`${setDef.name} concept art`}
              className="w-full object-contain"
              draggable="false"
              loading="lazy"
            />
          </div>
        </div>
      )}

      {setDef.preview && !isCorner && (
        <div className="mb-10 mx-auto max-w-[760px] relative">
          {/* Soft pulsing gold glow behind */}
          <div className="absolute inset-0 -z-0 flex items-center justify-center pointer-events-none">
            <div className="set-glow-pulse w-[80%] h-[80%] rounded-full" />
          </div>
          <div className="relative rounded-2xl border border-[#f5b400]/30 bg-gradient-to-b from-[#1a1410]/60 via-black/40 to-black/70 px-4 py-5 overflow-hidden">
            <div className="text-center mb-3">
              <p className="font-nav text-[10px] tracking-widest text-[#f5b400]">FULL SET PREVIEW</p>
              <p className="font-heading text-white text-lg mt-0.5">All Angles</p>
            </div>
            <div className="set-float">
              <img
                src={setDef.preview}
                alt={`${setDef.name} — equipment shown from multiple angles`}
                className="w-full max-w-[640px] mx-auto object-contain"
                draggable="false"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {recipes.map((r) => (
          <RecipeCard
            key={r.id}
            recipe={r}
            isOpen={openId === r.id}
            onToggle={() => setOpenId(openId === r.id ? null : r.id)}
          />
        ))}
      </div>
    </div>
  );
};

const Recipes = () => {
  const [openId, setOpenId] = useState(RECIPES[0]?.id || null);

  return (
    <div className="mb-24">
      <div className="mb-10 text-center">
        <p className="font-nav text-[#f5b400] text-xs tracking-widest">RECIPES</p>
        <h2 className="font-heading section-heading-white mt-3">EQUIPMENT</h2>
        <h2 className="font-heading section-heading-gold">CRAFTING</h2>
        <p className="mt-4 max-w-2xl mx-auto text-white/75 font-body text-sm">
          Master crafter? Gather these materials and forge legendary armor at any anvil across
          Eldoria.
        </p>
      </div>

      {RECIPE_SETS.map((setDef) => {
        const setRecipes = RECIPES.filter((r) => r.set === setDef.id);
        if (setRecipes.length === 0) return null;
        return (
          <SetSection
            key={setDef.id}
            setDef={setDef}
            recipes={setRecipes}
            openId={openId}
            setOpenId={setOpenId}
          />
        );
      })}
    </div>
  );
};

export default Recipes;
